import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/useApi";
import { useRBAC } from "../../hooks/useRBAC";
import { useModuleAvailability } from "../../hooks/useModuleAvailability";
import BulkAssignRiskModal from "../BulkAssignRiskModal";
import CreateJiraIssueModal from "../CreateJiraIssueModal";
import CreateServiceNowIncidentModal from "../CreateServiceNowIncidentModal";
import {
  BREAK_POINT, effortLabel, fetchWhatIf, formatDate, formatMoney, frontendRiskTypeOf, laneLabel, remediationTypeOf,
} from "./fusionApi";

/**
 * "◆ Break Point" box on the chain's break-point (FUSION.md §4.6.4): always a risk, the one whose
 * fix removes the most FAIR dollars per unit of effort across the chains it sits on. The headline
 * gives that reduction and the chain count, which opens the cross-chain list. The effort is marked
 * "(proposed)" while its factors are proposals. The break-point's `scenarioRemoved` is deliberately
 * not shown: it sums the scenario over every chain the risk reaches. Actions reuse the existing
 * assign / Jira / ServiceNow flows on the break-point risk, gated exactly like All Risks gates them;
 * afterwards Fusion re-reads with refresh=true (onActionDone).
 */
const FusionLeverageCard = ({ breakPoint, anchorType, anchorId, riskMembers, riskScope, onOpen, onActionDone }) => {
  const navigate = useNavigate();
  const apiCall = useApi();
  const { canManageOrganizationRisks, canManageDeal, canManageBusinessUnit } = useRBAC();
  const [modal, setModal] = useState(null); // null | "assign" | { kind: "jira"|"servicenow", rem }
  const [xchains, setXchains] = useState(null);
  // Jira / ServiceNow need internet access: their buttons are hidden where ticketing is unavailable.
  const ticketingAvailable = useModuleAvailability().isModuleAvailable("ticketing");

  if (!breakPoint) return null;

  const canManageRisk = (riskId) => {
    const s = riskScope[riskId] || {};
    return !!(canManageOrganizationRisks()
      || (s.businessUnitId && canManageBusinessUnit(s.businessUnitId))
      || (s.dealId && canManageDeal(s.dealId)));
  };
  const attached = [breakPoint.point.id];
  const manageable = attached.filter(canManageRisk);
  const riskById = Object.fromEntries(riskMembers.map((m) => [m.id, m]));
  // The break-point risk may sit beyond the drawn steps: fall back to its own label and type.
  const riskOf = (id) => riskById[id]
    || (id === breakPoint.point.id ? { label: breakPoint.point.label, riskType: breakPoint.riskType } : {});
  const remediations = breakPoint.remediations || [];
  const action = remediations[0]?.title || `Resolve ${breakPoint.point.label}`;
  const effort = effortLabel(breakPoint.effort, breakPoint.effortIsProposal);
  const chainsText = `${breakPoint.chainCount} chain${breakPoint.chainCount === 1 ? "" : "s"}`;
  const owner = remediations.find((r) => r.owner)?.owner;

  const toggleChains = async () => {
    if (xchains) { setXchains(null); return; }
    try {
      const r = await fetchWhatIf(apiCall, { anchorType, anchorId, nodeType: breakPoint.point.type, nodeId: breakPoint.point.id });
      setXchains(r?.affectedChains || []);
    } catch {
      setXchains([]);
    }
  };

  const ticketTarget = (rem) => {
    const risk = riskOf(rem.riskId);
    return {
      riskId: rem.riskId,
      riskTitle: risk.label,
      riskName: risk.label,
      userRating: risk.severity,
      remediationID: rem.remediationId,
      remediationTitle: rem.title,
      remediationPriority: rem.priority,
      remediationTargetDate: rem.targetDate,
      progressPercentage: rem.progressPercentage,
      jiraIssueKey: rem.jiraIssueKey,
      jiraIssueUrl: rem.jiraIssueUrl,
      serviceNowIncidentNumber: rem.serviceNowIncidentNumber,
      serviceNowIncidentUrl: rem.serviceNowIncidentUrl,
      remediationType: remediationTypeOf(risk.riskType),
    };
  };
  const firstRem = remediations.find((r) => canManageRisk(r.riskId));
  const closeAndRefresh = () => { setModal(null); onActionDone?.(); };

  return (
    <div className="rounded-lg border border-violet-300 bg-violet-50 px-3 py-2.5 my-3">
      <div className="text-sm font-semibold text-violet-700">
        {`◆ ${BREAK_POINT}: removes ${formatMoney(breakPoint.meanRemovedAcrossChains)} across `}
        <button className="text-indigo-600 hover:underline" onClick={toggleChains}>{chainsText}</button>
      </div>
      <div className="text-sm text-content-secondary">
        <span className="font-semibold text-content-primary">{action}</span>
        {" · "}{laneLabel(breakPoint.point.lane)}
        {owner && <> · owner {owner}</>}
      </div>
      {effort && <div className="text-xs text-content-secondary">Effort: {effort}</div>}

      {xchains && (
        <div className="mt-2 rounded-lg border border-violet-300 bg-theme-secondary p-2">
          <div className="text-xs font-semibold mb-1">Chains this fix reaches ({breakPoint.chainCount})</div>
          {xchains.length === 0 && <div className="text-xs text-content-muted">No other chain holds this point.</div>}
          {xchains.map((c) => (
            <button key={`${c.anchorType}:${c.anchorId}`} onClick={() => onOpen(c.anchorType, c.anchorId)}
              className="w-full flex items-center gap-2 text-left text-xs rounded border border-border px-2 py-1 mb-1 hover:bg-theme-hover">
              <span className="flex-1">{c.anchorLabel}</span>
              <span className="font-semibold">{formatMoney(c.meanBefore)}</span>
              <span className="text-content-muted">›</span>
            </button>
          ))}
        </div>
      )}

      {remediations.length > 0 && (
        <div className="text-xs text-content-secondary mt-1.5">
          {remediations.map((r) => (
            <div key={r.remediationId}>• {r.title} — {r.owner || "no owner"}{r.targetDate ? ` · target ${formatDate(r.targetDate)}` : ""} · {r.status}</div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 mt-2">
        {manageable.length > 0 && (
          <button onClick={() => setModal("assign")} className="text-xs px-2.5 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">Assign (email)</button>
        )}
        {firstRem && ticketingAvailable && (
          <>
            <button onClick={() => setModal({ kind: "servicenow", rem: firstRem })} className="text-xs px-2.5 py-1 rounded-lg border border-border bg-theme-secondary">ServiceNow</button>
            <button onClick={() => setModal({ kind: "jira", rem: firstRem })} className="text-xs px-2.5 py-1 rounded-lg border border-border bg-theme-secondary">Jira</button>
          </>
        )}
        {attached[0] && (
          <button onClick={() => navigate(`/risks?riskId=${encodeURIComponent(attached[0])}`)} className="text-xs px-2.5 py-1 rounded-lg border border-border bg-theme-secondary">Open risk</button>
        )}
      </div>

      {modal === "assign" && (
        <BulkAssignRiskModal
          show
          onClose={() => setModal(null)}
          risks={manageable.map((id) => ({
            id, riskId: id, riskTitle: riskOf(id).label, dealId: riskScope[id]?.dealId || null,
            // Per row: the activity log is only written with a type.
            riskType: frontendRiskTypeOf(riskOf(id).riskType),
          }))}
          riskType={null}
          refreshData={closeAndRefresh}
        />
      )}
      {modal?.kind === "jira" && (
        <CreateJiraIssueModal isOpen onClose={() => setModal(null)} remediation={ticketTarget(modal.rem)}
          remediationType={ticketTarget(modal.rem).remediationType} onSuccess={closeAndRefresh} />
      )}
      {modal?.kind === "servicenow" && (
        <CreateServiceNowIncidentModal isOpen onClose={() => setModal(null)} remediation={ticketTarget(modal.rem)}
          remediationType={ticketTarget(modal.rem).remediationType} onSuccess={closeAndRefresh} />
      )}
    </div>
  );
};

export default FusionLeverageCard;
